import Layer from "./Layer";
import Rect from "./Rect";
import Vector2 from "./Vector2";
import Scene from "./Scene";
import { pointInPolygon } from "./Util";
import { observable } from "mobx";
import ConnectionLayer from "./ConnectionLayer";
import Handlebars from "handlebars";

Handlebars.registerHelper("ifeq", function (arg1, arg2, options) {
  return arg1 == arg2 ? options.fn(this) : options.inverse(this);
});

export const nodeColors = [
  {
    bg: "#D3455B",
    fg: "white",
  },

  {
    bg: "#E8833A",
    fg: "white",
  },

  {
    bg: "#2C88D9",
    fg: "white",
  },

  {
    bg: "#1AAE9F",
    fg: "white",
  },

  {
    bg: "#BD34D1",
    fg: "white",
  },

  {
    bg: "#F7C325",
    fg: "black",
  },

  {
    bg: "#788896",
    fg: "white",
  },

  {
    bg: "#4B5C6B",
    fg: "white",
  },
];

export const questionTypes = {
  text: "Free Response",
  yesNo: "Yes/No",
  singleChoice: "Single Response",
  multiChoice: "Multiple Response",
  noResponse: "Without Response",
};

export const narrativeSplitPoint = "${$f}";

export default class NodeLayer extends Layer {
  @observable
  private _questionText = "";

  @observable
  private _colorCode = 0;

  @observable
  private _questionType = questionTypes.text;

  @observable
  private _choices = new Array<string>();

  @observable
  private _choicesStyles = new Array<string>();

  @observable
  private _yesChoiceStyle = "";

  @observable
  private _noChoiceStyle = "";

  @observable
  private _answers = new Array<string>();

  @observable
  private _uploadedFilesNames = new Array<string>();

  @observable
  private _narrativeTemplate = "";

  @observable
  private _narrativeCompileError = "";

  @observable
  private _finalMessage = "";

  @observable
  private _enabledReferralUrl = false;

  @observable
  private _enabledFinalQuestion = false;

  @observable
  private _enabledName = true;

  @observable
  private _redirectUrl = "";

  private _arrowOpen = false;

  public get arrowOpen(): boolean {
    return this._arrowOpen;
  }

  public get finalMessage(): string {
    return this._finalMessage;
  }

  public setFinalMessage(message: string) {
    this._finalMessage = message;
  }

  public setFinalQuestion(state: boolean) {
    this._enabledFinalQuestion = state;
  }
  public setEnabledReferralUrl(state: boolean) {
    this._enabledReferralUrl = state;
  }

  public get finalQuestionStatus(): boolean {
    return this._enabledFinalQuestion;
  }

  public get referralUrlStatus(): boolean {
    return this._enabledReferralUrl;
  }

  public get enabledName(): boolean {
    return this._enabledName;
  }

  public setEnabledName(value: boolean) {
    this._enabledName = value;
  }

  public get redirectUrl(): string {
    return this._redirectUrl;
  }

  public setRedirectUrl(url: string) {
    this._redirectUrl = url;
  }

  private _templateDelegate: Handlebars.TemplateDelegate;

  public templateDelegate(value: any): string {
    if (this._templateDelegate == null) {
      try {
        this._templateDelegate = Handlebars.compile(this._narrativeTemplate);
        this._templateDelegate({ value: ["dummy"] });
      } catch (err) {
        this._templateDelegate = null;

        return "";
      }
    }
    return this._templateDelegate(value);
  }

  public get narrativeCompileError(): string {
    return this._narrativeCompileError;
  }

  public get narrativeTemplate(): string {
    return this._narrativeTemplate;
  }

  public setNarrativeTemplate(template: string) {
    this._narrativeTemplate = template;
  }

  public compileNarrativeTemplate() {
    try {
      this._templateDelegate = Handlebars.compile(this._narrativeTemplate);
      this._templateDelegate({ value: ["dummy"] });
      this._narrativeCompileError = "";
    } catch (err) {
      this._templateDelegate = null;
      this._narrativeCompileError = err.message;
    }
  }

  public get answers(): Array<string> {
    return Array.from(this._answers);
  }
  public getAnswersBasedOnQuestType(
    questionType: string,
    answer: any
  ): Array<string> {
    let answers: any = null;
    if (questionType !== questionTypes.multiChoice) {
      answers = [answer];
    } else {
      answers = [...answer];
    }
    return answers;
  }

  public get uploadedFilesNames(): Array<string> {
    return Array.from(this._uploadedFilesNames);
  }

  public get answer(): string {
    if (this._answers.length == 0) {
      return "";
    }
    return this._answers[0];
  }

  public setAnswer(answer: string) {
    this._answers = [answer];
  }

  public setAnswers(arr: Array<string>) {
    this._answers = arr;
  }
  public setUploadedFilesNames(arr: Array<string>) {
    this._uploadedFilesNames = arr;
  }

  private _minSize = new Vector2();
  private _outgoingConnections = new Set<ConnectionLayer>();
  private _incomingConnections = new Set<ConnectionLayer>();

  public addChoice(choice: string) {
    this._choices.push(choice);
  }

  public removeChoice(idx: number) {
    this._choices.splice(idx, 1);
    // for (var conn of this.outgoingConnections) {
    //   var conditionInvalid = this._choices.filter(conn.condition);
    //   if (conditionInvalid) {
    //     conn.setCondition('');
    //   }
    // }
    this.outgoingConnections.forEach((conn) => {
      const conditionFounded = this._choices.filter(
        (choice) => `${narrativeSplitPoint}${choice}` === conn.condition
      );
      if (conditionFounded.length === 0) {
        conn.setCondition("");
      }
    });
    // this.reconcileChoices();
  }

  public setChoice(idx: number, value: string) {
    if (idx >= this._choices.length) {
      return;
    }
    this._choices[idx] = value;
    //this.reconcileChoices();
  }

  public setChoices(choices: Array<string>) {
    this._choices = choices;
    this.reconcileChoices();
  }

  private reconcileChoices() {
    for (var conn of this.outgoingConnections) {
      var conditionInvalid = this._choices.indexOf(conn.condition) == -1;
      if (conditionInvalid) {
        conn.setCondition("");
      }
    }
  }

  public get choices(): Array<string> {
    return Array.from(this._choices);
  }

  public get choicesStyles(): Array<string> {
    return Array.from(this._choicesStyles);
  }

  public get yesChoiceStyle(): string {
    return this._yesChoiceStyle;
  }

  public get noChoiceStyle(): string {
    return this._noChoiceStyle;
  }

  public setChoicesStyles(choicesStyles: Array<string>) {
    this._choicesStyles = choicesStyles;
  }

  public setChoiceStyle(idx: number, value: string) {
    this._choicesStyles[idx] = value;
  }

  public setYesChoiceStyle(value: string) {
    return (this._yesChoiceStyle = value);
  }

  public setNoChoiceStyle(value: string) {
    return (this._noChoiceStyle = value);
  }

  public get hasChoices(): boolean {
    return (
      this.questionType == questionTypes.singleChoice ||
      this.questionType == "Single Choice" ||
      this.questionType == "Multiple Choice" ||
      this.questionType == questionTypes.yesNo ||
      this.questionType == questionTypes.multiChoice
    );
  }

  public addOutgoingConnection(conn: ConnectionLayer) {
    this._outgoingConnections.add(conn);
  }

  public removeOutgoingConnection(conn: ConnectionLayer) {
    this._outgoingConnections.delete(conn);
  }

  public get outgoingConnections(): Array<ConnectionLayer> {
    return Array.from(this._outgoingConnections);
  }

  public addIncomingConnection(conn: ConnectionLayer) {
    this._incomingConnections.add(conn);
  }

  public removeIncomingConnection(conn: ConnectionLayer) {
    this._incomingConnections.delete(conn);
  }

  public get incomingConnections(): Array<ConnectionLayer> {
    return Array.from(this._incomingConnections);
  }

  public get minSize(): Vector2 {
    return this._minSize;
  }

  public set minSize(ms: Vector2) {
    this._minSize = ms;
  }

  public get questionText(): string {
    return this._questionText;
  }

  public setQuestionText(txt: string) {
    this._questionText = txt;
  }

  public get questionType(): string {
    return this._questionType;
  }

  public setQuestionType(type: string) {
    if (this._questionType == type) {
      return;
    }

    // last changes need a verification
    if (
      (this._questionType == questionTypes.singleChoice &&
        type == questionTypes.multiChoice) ||
      (this.questionType == questionTypes.multiChoice &&
        type == questionTypes.singleChoice) ||
      (this.questionType == "Multiple Choice" && type == "Multiple Response") ||
      (this.questionType == "Single Choice" && type == "Single Response") ||
      (this._questionType == "Single Choice" && type == "Multiple Choice") ||
      (this.questionType == "Multiple Choice" && type == "Single Choice")
    ) {
      this._questionType = type;
    } else {
      this._questionType = type;

      this._choices = [];
      if (
        type == questionTypes.multiChoice ||
        type == "Single Choice" ||
        type == "Multiple Choice" ||
        type == questionTypes.singleChoice
      ) {
        this.addChoice("");
      }
      this.reconcileChoices();

      if (type == questionTypes.yesNo) {
        this.setChoices(["Yes", "No"]);
      }
    }
  }

  public get colorCode(): number {
    return this._colorCode;
  }

  public setColorCode(val: number) {
    this._colorCode = val;
  }

  public get bgColor(): string {
    return nodeColors[this._colorCode].bg;
  }

  public get fgColor(): string {
    return nodeColors[this._colorCode].fg;
  }

  public constructor(scene: Scene, id: string, rect: Rect) {
    super(scene, id, rect);
  }

  public checkPointIntersection(point: Vector2): boolean {
    return this.checkBoundingBoxIntersection(point);
  }

  private getArrowBoundingBox(): Array<Vector2> {
    var outerRect = this.boundingRect;
    var fontSize = 28;

    var startPoint = outerRect.position.add(
      new Vector2(
        outerRect.size.x - fontSize * 2,
        outerRect.size.y - fontSize * 2
      )
    );

    var endPoint = outerRect.position.add(outerRect.size);

    var rect = Rect.fromExtents(startPoint, endPoint);

    var ret = new Array<Vector2>(4);

    ret[0] = rect.position;
    ret[1] = rect.position.add(new Vector2(rect.size.x, 0));
    ret[2] = rect.position.add(rect.size);
    ret[3] = rect.position.add(new Vector2(0, rect.size.y));

    return ret;
  }

  public handleArrowClick(point: Vector2) {
    var points = this.getArrowBoundingBox();
    var arrowClicked = pointInPolygon(points, point);
    if (!arrowClicked) {
      return;
    }

    this._arrowOpen = !this._arrowOpen;
  }
}
